import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/helpers";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Calculator, LockKey, CaretDown, CaretRight, Warning, ArrowsClockwise,
  Users, HandCoins, TrendUp, Coins,
} from "@phosphor-icons/react";

const MODEL_LABEL = {
  monthly: "Monthly",
  daily: "Daily",
  hourly: "Hourly",
  part_time: "Part time",
  visiting: "Visiting",
  session_based: "Session based",
};

/**
 * Payroll Compute — Iter-42 rewrite.
 *
 * Now backed by `/api/payroll-engine/*` (Phase 4). Reads each employee's
 * `compensation_lines[]` + attendance data + Salary Rule Builder rules
 * and returns a per-employee breakdown. The legacy therapist-session
 * engine is no longer surfaced.
 */
export default function PayrollPage() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [showCompute, setShowCompute] = useState(false);
  const [computeForm, setComputeForm] = useState({
    period_start: monthStart(), period_end: monthEnd(), branch_id: "",
  });
  const [preview, setPreview] = useState(null);
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [openRun, setOpenRun] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    try {
      const [r, b] = await Promise.all([
        api.get("/payroll-engine/runs"),
        api.get("/branches"),
      ]);
      setRuns(r.data || []);
      setBranches(b.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // When the dialog opens for a NEW compute session, refresh the default
  // dates to the current month so a payroll admin returning next month
  // never sees a stale window.
  useEffect(() => {
    if (showCompute && !preview) {
      setComputeForm(f => ({ ...f, period_start: monthStart(), period_end: monthEnd() }));
    }
  }, [showCompute, preview]);

  const compute = async () => {
    setComputing(true); setPreview(null);
    try {
      const { data } = await api.post("/payroll-engine/preview", {
        period_start: computeForm.period_start,
        period_end: computeForm.period_end,
        branch_id: computeForm.branch_id || null,
      });
      setPreview(data);
      if ((data.employees || []).length === 0 && (data.excluded_employees || []).length === 0) {
        toast.info("No active employees to include in this period.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setComputing(false); }
  };

  const saveDraft = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const { data } = await api.post("/payroll-engine/runs", {
        period_start: preview.period_start,
        period_end: preview.period_end,
        branch_id: preview.branch_id,
      });
      toast.success("Payroll draft saved");
      setShowCompute(false); setPreview(null);
      if (data?.id) {
        // Iter-47 — jump straight into the Payroll Review Center.
        navigate(`/payroll/runs/${data.id}`);
      } else {
        load();
      }
    } catch (e) {
      // Surface duplicate-run guard message clearly.
      const detail = e?.response?.data?.detail;
      if (detail?.code === "duplicate_run_for_month") {
        toast.error(detail.message, {
          duration: 8000,
          action: detail.existing_run_id
            ? { label: "Open existing", onClick: () => navigate(`/payroll/runs/${detail.existing_run_id}`) }
            : undefined,
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1.5">Payroll</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Payroll Compute</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Computes gross earnings from each employee&apos;s compensation line items,
            applies Salary Rule Builder outputs, and produces a reviewable draft run.
          </p>
        </div>
        <Button size="lg" className="gap-1.5 w-full sm:w-auto" onClick={() => setShowCompute(true)} data-testid="new-run-btn">
          <Calculator size={16} weight="bold" /> New payroll run
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <div className="text-sm font-medium">Draft &amp; Approved Runs</div>
            <Badge variant="outline" className="text-[10px]">{runs.length}</Badge>
          </div>
          {runs.length === 0 ? (
            <div className="px-5 py-14 text-center text-sm text-muted-foreground">
              No payroll runs yet. Click <b>New payroll run</b> to compute your first one from configured employee compensation.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {runs.map(r => (
                <button
                  key={r.id}
                  onClick={() => navigate(`/payroll/runs/${r.id}`)}
                  className="w-full px-5 py-3 flex items-center gap-4 hover:bg-muted/40 text-left"
                  data-testid={`run-row-${r.id}`}
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {r.period_start} → {r.period_end}
                      {r.status === "dispatched" ? (
                        <Badge className="text-[10px] bg-blue-100 text-blue-800 border-blue-200">Dispatched</Badge>
                      ) : r.status === "approved" ? (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">Approved</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">Draft</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {(r.totals?.net || 0) > 0 ? `Net ${fmtMoney(r.totals.net)}` : "—"} · {r.active_rule_count || 0} active rules · created {(r.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-bold tabular-nums">{fmtMoney(r.totals?.net || 0)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Net payroll</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compute dialog */}
      <Dialog open={showCompute} onOpenChange={(o) => { setShowCompute(o); if (!o) { setPreview(null); setExpanded({}); } }}>
        <DialogContent className="bg-card max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="compute-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Compute payroll</DialogTitle>
            <DialogDescription className="text-xs">
              Pick a date range and branch. The engine reads each employee&apos;s configured
              compensation lines, attendance in the period, and applies all active Salary
              Rule Builder rules to produce the preview below. Nothing is saved until you
              click <b>Save as draft</b>.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
            <div className="space-y-1.5">
              <Label>From</Label>
              <Input type="date" value={computeForm.period_start}
                onChange={e => setComputeForm(f => ({ ...f, period_start: e.target.value }))}
                data-testid="run-from" />
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input type="date" value={computeForm.period_end}
                onChange={e => setComputeForm(f => ({ ...f, period_end: e.target.value }))}
                data-testid="run-to" />
            </div>
            <div className="space-y-1.5">
              <Label>Branch</Label>
              <Select value={computeForm.branch_id || "all"}
                onValueChange={v => setComputeForm(f => ({ ...f, branch_id: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="run-branch"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="all">All branches</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={compute} disabled={computing}
              className="gap-1.5" data-testid="run-compute">
              {computing ? <ArrowsClockwise size={16} className="animate-spin" /> : <Calculator size={16} weight="bold" />}
              {computing ? "Computing…" : "Compute"}
            </Button>
          </div>

          {preview && <ComputePreview preview={preview} expanded={expanded} setExpanded={setExpanded} />}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCompute(false)}>Close</Button>
            {preview && (
              <Button
                onClick={saveDraft}
                disabled={
                  saving
                  || (preview.employees || []).length === 0
                  || (preview.conflicts || []).length > 0
                }
                title={(preview.conflicts || []).length > 0 ? "Resolve rule conflicts before saving" : undefined}
                className="gap-1.5"
                data-testid="run-save"
              >
                <LockKey size={14} /> {saving ? "Saving…" : "Save as draft"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run detail (read-only preview of a saved run) */}
      <Dialog open={!!openRun} onOpenChange={(o) => { if (!o) setOpenRun(null); }}>
        <DialogContent className="bg-card max-w-6xl max-h-[92vh] overflow-y-auto">
          {openRun && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {openRun.period_start} → {openRun.period_end}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {openRun.status === "approved" ? "Approved" : "Draft"} run · {openRun.active_rule_count} active rules
                </DialogDescription>
              </DialogHeader>
              <ComputePreview preview={openRun} expanded={expanded} setExpanded={setExpanded} readOnly />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Preview: summary tiles + main table + excluded warning list
// ============================================================
function ComputePreview({ preview, expanded, setExpanded, readOnly = false }) {
  const totals = preview.totals || {};
  const rows = preview.employees || [];
  const excluded = preview.excluded_employees || [];
  const conflicts = preview.conflicts || [];

  // Ruleset audits grouped by employee for the expandable row.
  const auditByEmp = useMemo(() => {
    const map = {};
    for (const a of (preview.audit_trail || [])) {
      (map[a.employee_id] ||= []).push(a);
    }
    return map;
  }, [preview.audit_trail]);

  const totalGrossEarnings = round2(
    rows.reduce((s, e) => s + (e.base_salary || 0) + (e.line_totals?.deductions || 0), 0)
  );
  const totalDeductions = round2(
    (totals.deductions || 0)
    + rows.reduce((s, e) => s + (e.line_totals?.deductions || 0), 0)
  );
  const totalRuleAdj = round2(
    (totals.bonuses || 0) + (totals.commissions || 0)
    + (totals.overtime || 0) + (totals.allowances || 0)
    - (totals.deductions || 0)
  );

  return (
    <div className="mt-4 space-y-4">
      {/* Rule conflicts banner — surfaced BEFORE the payroll figures so
          admins never approve a run whose totals include double-applied
          rules. Backend detects conflicts when two rules share type +
          trigger.variable + trigger.operator + trigger.value + scope. */}
      {conflicts.length > 0 && (
        <div className="border-2 border-rose-400 bg-rose-50 rounded-md p-4" data-testid="conflicts-banner">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-none">
              <Warning size={22} weight="fill" className="text-rose-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-rose-900">
                {conflicts.length} rule conflict{conflicts.length === 1 ? "" : "s"} detected — resolve before approving
              </div>
              <p className="text-xs text-rose-800 mt-0.5">
                The following pairs share the same type, trigger, and scope, so their
                outputs would double-apply. Deactivate or narrow one rule in each pair
                on the <b>Salary Rule Builder</b> page.
              </p>
              <ul className="mt-2 space-y-1.5" data-testid="conflicts-list">
                {conflicts.map((c, i) => (
                  <li
                    key={`${c.rule_a?.id}-${c.rule_b?.id}-${i}`}
                    className="text-xs bg-white/70 border border-rose-200 rounded px-2.5 py-1.5"
                    data-testid={`conflict-${i}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-900 bg-white">
                        {c.rule_a?.name || c.rule_a?.id || "Rule A"}
                      </Badge>
                      <span className="text-rose-700 font-semibold">⇔</span>
                      <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-900 bg-white">
                        {c.rule_b?.name || c.rule_b?.id || "Rule B"}
                      </Badge>
                    </div>
                    {c.reason && (
                      <div className="text-[11px] text-rose-800 mt-1">{c.reason}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryTile icon={Users} label="Employees included" value={rows.length}
          tone="blue" testid="tile-employees" />
        <SummaryTile icon={HandCoins} label="Total gross earnings"
          value={fmtMoney(totalGrossEarnings)} tone="emerald" testid="tile-gross" />
        <SummaryTile icon={Coins} label="Total deductions"
          value={fmtMoney(totalDeductions)} tone="rose" testid="tile-deductions" />
        <SummaryTile icon={TrendUp} label="Net payroll"
          value={fmtMoney(totals.net || 0)} tone="primary" testid="tile-net" />
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-md px-4 py-8 text-center text-sm text-muted-foreground">
          No employees with configured compensation matched the selected period + branch.
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden" data-testid="compute-table">
          {/* Header */}
          <div className="grid grid-cols-[28px,minmax(0,2fr),110px,130px,140px,160px,140px] gap-2 px-3 py-2 bg-muted/40 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <div />
            <div>Employee</div>
            <div>Model</div>
            <div className="text-right">Gross</div>
            <div className="text-right">Attendance ded.</div>
            <div className="text-right">Rule adjustments</div>
            <div className="text-right">Net salary</div>
          </div>

          <div className="divide-y divide-border">
            {rows.map(emp => {
              const empId = emp.employee_id;
              const isOpen = !!expanded[empId];
              const noAttendance = emp.metrics_snapshot?.no_attendance_data
                || ((emp.metrics_snapshot?.attendance_days_present ?? 0) === 0
                    && (emp.metrics_snapshot?.leave_days_taken ?? 0) === 0
                    && (emp.metrics_snapshot?.total_hours_worked ?? 0) === 0);
              const attDed = round2(emp.line_totals?.deductions || 0);
              const grossEarnings = round2((emp.base_salary || 0) + attDed);
              const ruleAdj = round2(
                (emp.bonuses_total || 0) + (emp.commissions_total || 0)
                + (emp.overtime_total || 0) + (emp.allowances_total || 0)
                - (emp.deductions_total || 0)
              );
              const rules = auditByEmp[empId] || [];
              return (
                <div key={empId} className="text-sm">
                  <button
                    className="w-full grid grid-cols-[28px,minmax(0,2fr),110px,130px,140px,160px,140px] gap-2 px-3 py-3 items-center hover:bg-muted/30 text-left"
                    onClick={() => setExpanded(e => ({ ...e, [empId]: !e[empId] }))}
                    data-testid={`emp-row-${empId}`}
                  >
                    <div className="text-muted-foreground">
                      {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {emp.employee_number && (
                          <span className="font-mono text-[11px] text-muted-foreground mr-2" data-testid={`payroll-empnum-${emp.employee_id}`}>
                            {emp.employee_number}
                          </span>
                        )}
                        {emp.employee_name}
                      </div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                        {noAttendance && (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <Warning size={11} weight="fill" /> No attendance data
                          </span>
                        )}
                        {emp.base_note && !noAttendance && (
                          <span className="truncate">{emp.base_note}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs">
                      <Badge variant="outline" className="text-[10px]">
                        {MODEL_LABEL[emp.compensation_model] || emp.compensation_model || "—"}
                      </Badge>
                    </div>
                    <div className="text-right tabular-nums font-medium">{fmtMoney(grossEarnings)}</div>
                    <div className="text-right tabular-nums text-rose-700">{attDed > 0 ? `− ${fmtMoney(attDed)}` : "—"}</div>
                    <div className={`text-right tabular-nums ${ruleAdj < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {ruleAdj === 0 ? "—" : (ruleAdj > 0 ? "+ " : "− ") + fmtMoney(Math.abs(ruleAdj))}
                    </div>
                    <div className="text-right tabular-nums font-display font-bold text-base">
                      {fmtMoney(emp.net || 0)}
                    </div>
                  </button>

                  {isOpen && (
                    <EmployeeBreakdown emp={emp} rules={rules} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Table footer */}
          <div className="grid grid-cols-[28px,minmax(0,2fr),110px,130px,140px,160px,140px] gap-2 px-3 py-3 bg-muted/40 text-xs font-semibold items-center border-t border-border">
            <div />
            <div className="uppercase tracking-wide text-muted-foreground">Workspace total</div>
            <div className="text-[10px] text-muted-foreground">—</div>
            <div className="text-right tabular-nums">{fmtMoney(totalGrossEarnings)}</div>
            <div className="text-right tabular-nums text-rose-700">− {fmtMoney(totalDeductions)}</div>
            <div className={`text-right tabular-nums ${totalRuleAdj < 0 ? "text-rose-700" : "text-emerald-700"}`}>
              {totalRuleAdj === 0 ? "—" : (totalRuleAdj > 0 ? "+ " : "− ") + fmtMoney(Math.abs(totalRuleAdj))}
            </div>
            <div className="text-right tabular-nums font-display font-bold text-base">{fmtMoney(totals.net || 0)}</div>
          </div>
        </div>
      )}

      {/* Excluded (unconfigured) employees warning */}
      {excluded.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-4" data-testid="excluded-warning">
          <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
            <Warning size={16} weight="fill" />
            {excluded.length} employee{excluded.length === 1 ? "" : "s"} excluded — compensation not configured
          </div>
          <p className="text-xs text-amber-800 mt-1">
            These employees have no compensation line items on their Employee Master
            record. Add at least one earning line before they can be included in a
            payroll run.
          </p>
          <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {excluded.map(x => (
              <div key={x.employee_id} className="text-xs bg-white/60 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-2">
                {x.employee_number && (
                  <span className="font-mono text-[10px] text-muted-foreground">{x.employee_number}</span>
                )}
                <span className="font-medium truncate flex-1">{x.employee_name}</span>
                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-900">
                  Not configured
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {readOnly && (
        <p className="text-[11px] text-muted-foreground italic">
          Read-only view of a saved payroll draft. Approve / reject in the Payroll Review Center.
        </p>
      )}
    </div>
  );
}

// ============================================================
// Expandable per-employee breakdown
// ============================================================
function EmployeeBreakdown({ emp, rules }) {
  const detail = emp.line_totals?.detail || { earnings: [], deductions: [] };
  const metrics = emp.metrics_snapshot || {};

  const earnings = detail.earnings || [];
  const deductions = detail.deductions || [];
  const bonuses = rules.filter(r => ["bonus", "commission", "overtime", "allowance"].includes(r.type));
  const ruleDeducts = rules.filter(r => r.type === "deduction");

  // Total pre-deduction earnings so the visible math actually reconciles.
  // Backend `emp.gross` = base_salary (post-line-deductions) + bonus rules.
  // We rebuild it here so the row-level Gross equals sum(earnings) + sum(bonus_rules).
  const grossEarnings = round2(
    (emp.base_salary || 0)
    + (emp.line_totals?.deductions || 0)
    + bonuses.reduce((s, r) => s + (r.amount || 0), 0)
  );

  return (
    <div className="bg-muted/20 border-t border-border px-4 py-3" data-testid={`emp-detail-${emp.employee_id}`}>
      <div className="grid gap-4 md:grid-cols-3">
        {/* Earnings */}
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-800">Earnings line items</div>
          {earnings.length === 0 && <div className="text-xs text-muted-foreground italic">No earning lines</div>}
          {earnings.map((l, i) => (
            <div key={`earn-${l.code || l.label || i}`} className="text-xs flex items-center justify-between gap-2">
              <span className="truncate">{l.label || "—"}</span>
              <span className="tabular-nums text-emerald-800 font-medium">{fmtMoney(l.amount)}</span>
            </div>
          ))}
          {bonuses.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-800 pt-2">Rule adjustments (+)</div>
              {bonuses.map(r => (
                <div key={r.rule_id} className="text-xs flex items-center justify-between gap-2">
                  <span className="truncate">{r.rule_name} <Badge variant="outline" className="text-[9px] ml-1">{r.type}</Badge></span>
                  <span className="tabular-nums text-emerald-800 font-medium">+ {fmtMoney(r.amount)}</span>
                </div>
              ))}
            </>
          )}
          <div className="pt-1.5 border-t border-border/60 flex items-center justify-between text-xs font-semibold">
            <span>Gross</span>
            <span className="tabular-nums">{fmtMoney(grossEarnings)}</span>
          </div>
        </div>

        {/* Deductions */}
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-rose-800">Deductions</div>
          {deductions.length === 0 && ruleDeducts.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No deductions</div>
          )}
          {deductions.map((l, i) => (
            <div key={`ded-${l.code || l.label || i}`} className="text-xs flex items-center justify-between gap-2">
              <span className="truncate">{l.label || "—"}</span>
              <span className="tabular-nums text-rose-800 font-medium">− {fmtMoney(l.amount)}</span>
            </div>
          ))}
          {ruleDeducts.map(r => (
            <div key={r.rule_id} className="text-xs flex items-center justify-between gap-2">
              <span className="truncate">{r.rule_name} <Badge variant="outline" className="text-[9px] ml-1">rule</Badge></span>
              <span className="tabular-nums text-rose-800 font-medium">− {fmtMoney(r.amount)}</span>
            </div>
          ))}
          <div className="pt-1.5 border-t border-border/60 flex items-center justify-between text-xs font-semibold">
            <span>Net Salary</span>
            <span className="tabular-nums text-base font-display font-bold">{fmtMoney(emp.net || 0)}</span>
          </div>
        </div>

        {/* Attendance snapshot */}
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-blue-800">Attendance in period</div>
          <MetricRow label="Days present" value={metrics.attendance_days_present ?? 0} />
          <MetricRow label="Leave days" value={metrics.leave_days_taken ?? 0} />
          <MetricRow label="Unpaid leave" value={metrics.unpaid_leave_days ?? 0} tone="rose" />
          <MetricRow label="Late arrivals" value={metrics.late_arrivals ?? 0} />
          <MetricRow label="Early exits" value={metrics.early_exits ?? 0} />
          <MetricRow label="Hours worked" value={`${(metrics.total_hours_worked ?? 0)} h`} />
          <MetricRow label="Overtime" value={`${(metrics.overtime_hours ?? 0)} h`} tone="emerald" />
          <MetricRow label="Days active" value={`${(metrics.days_active_in_period ?? metrics.period_working_days ?? 0)} / ${(metrics.period_working_days ?? 0)}`} />
        </div>
      </div>
    </div>
  );
}

function MetricRow({ label, value, tone }) {
  const color = tone === "rose" ? "text-rose-700" : tone === "emerald" ? "text-emerald-700" : "text-foreground";
  return (
    <div className="text-xs flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums font-medium ${color}`}>{value}</span>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, tone, testid }) {
  const toneMap = {
    blue:    "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
    primary: "bg-primary/10 text-primary border-primary/30",
  };
  return (
    <div className={`border rounded-md px-4 py-3 ${toneMap[tone] || ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold opacity-80">
        <Icon size={14} weight="duotone" /> {label}
      </div>
      <div className="text-2xl font-display font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

// ---- helpers ------------------------------------------------
function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }

function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function monthEnd() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${last}`;
}
